// KPI Builder — one query builder per recipe. Each copies the shape and aggregation names of an
// existing, working visualization, so no backend or MIS app change is needed.
import { INDEX_PREFIX, LOCATION_MAP } from 'constants/kpiBuilderCatalogue';
import { columnAggs, sortColumn } from './columns';
import { filterClause, resolveSource, splitFilters } from './custom';
import { groupById, NOT_COMPLETED_ID } from './learnerGroups';
import { chosenKpiFilters, listColumns, selectedGroups } from './lookups';
import { BuilderAnswers, Column, DataSource, EsNode, GroupBy, LearnerGroup, Measure, QueryObject, SourceId } from './types';

export interface RecipeContext {
  a: BuilderAnswers;
  code: string;
  chartType: string;
  cumulative: boolean;
  src: DataSource;
  srcId: SourceId;
  srcBId: SourceId;
  measure: Measure | null;
  group: GroupBy;
  metric: EsNode;
  metricName: string;
}

export interface RecipeResult {
  queries: QueryObject[];
  cfg: Record<string, unknown>;
  /** Backend handler the visualization needs, for recipes whose chart is drawn by one. */
  handlerKey?: string;
}

type EsClause = Record<string, unknown>;

/** Group fields that hold numbers, where a blank-string term would be rejected. */
export const NUMERIC_GROUP_FIELDS = ['levels', 'attemptNumber'];

export function clauseFor(g: LearnerGroup, src: DataSource): EsClause {
  if (g.lookup) return { terms: { [src.comp as string]: { index: INDEX_PREFIX + 'drishti-active-competency-list', id: 'current', path: 'competencyIds' } } };
  return { terms: { [g.field as string]: g.values } };
}

/** @param cadreOnly Keep only the cadre groups (in-service / pre-service), as the first list of a gap does. */
export function filterFor(srcId: SourceId, a: BuilderAnswers, cadreOnly = false): EsClause {
  const src = resolveSource(srcId, a);
  const groups = selectedGroups(a).filter((g) => g.applies.includes(srcId) && (!cadreOnly || g.field === 'profession'));
  // Typed conditions narrow the main data only, never the second list of a gap.
  const typed = srcId === a.src && !cadreOnly ? splitFilters(a.custom.filters) : { must: [], mustNot: [] };
  const clauses = [...groups.map((g) => clauseFor(g, src)), ...typed.must];
  if (!clauses.length && !typed.mustNot.length) return { match_all: {} };
  return { bool: { filter: clauses, ...(typed.mustNot.length ? { must_not: typed.mustNot } : {}) } };
}

/** The condition for “who has this trait”: a saved learner group, or one the admin typed. */
export function traitClause(a: BuilderAnswers, src: DataSource): EsClause {
  if (a.rateSeg === 'custom') return filterClause(a.custom.rateWhen);
  return clauseFor(groupById(a.rateSeg as string) as LearnerGroup, src);
}

export function envelope(srcId: SourceId, a: BuilderAnswers): Omit<QueryObject, 'aggrQuery'> {
  const own = resolveSource(srcId, a);
  const rqm: Record<string, string> = { ...LOCATION_MAP };
  chosenKpiFilters(a).forEach((f) => {
    const field = f.field ?? own.comp;
    if (field) rqm[f.key] = field;
  });
  return { module: 'COMMON', dataSource: 'ES', requestQueryMap: rqm, dateRefField: own.date, indexName: INDEX_PREFIX + own.index, esInstance: 'QUADNARY' };
}

/** The filter without the documents that have no value for the group (a blank block, say), which would otherwise show as the top bar. */
export function withoutBlank(filter: EsClause, g: GroupBy): EsClause {
  if (NUMERIC_GROUP_FIELDS.includes(g.field)) return filter;
  const blank = [{ term: { [g.field]: '' } }];
  const bool = (filter as { bool?: { filter?: EsClause[]; must_not?: EsClause[] } }).bool;
  if (!bool) return { bool: { must_not: blank } };
  return { bool: { ...bool, must_not: [...(bool.must_not ?? []), ...blank] } };
}

/** Name of a per-group aggregation, e.g. facilityType becomes by_facility_type. */
export const snake = (id: string): string => 'by_' + id.replace(/([A-Z])/g, '_$1').toLowerCase();

const PLURAL: Record<string, string> = {
  district: 'districts', block: 'blocks', facility: 'facilities', institute: 'institutes',
  facilityType: 'facility_types', competency: 'competencies', course: 'by_course', question: 'questions',
};

// shape of drishtiTotalUserReg / competencyEnrolled
export function buildCount(c: RecipeContext): RecipeResult {
  const { a, code, metric, metricName } = c;
  const inner = metricName === 'unique_users' ? 'user_count' : metricName;
  const aggs = { [code]: { filter: filterFor(c.srcId, a), aggs: { [inner]: metric } } };
  return { queries: [{ ...envelope(c.srcId, a), aggrQuery: { size: 0, aggs } }], cfg: { aggregationPaths: [code] } };
}

// shape of topPerformDistrictsProfessionals / totalRegisteredstudentsMap
export function buildPerGroup(c: RecipeContext): RecipeResult {
  const { a, group: g, metric, metricName } = c;
  const gname = PLURAL[g.id] ?? 'groups';
  const isMap = c.chartType === 'indiadistrictmap';
  const bucket = `aggregations.filtered_data.${gname}.buckets`;
  const filterPath = isMap ? bucket : ['key', 'doc_count', metricName + '.value'].map((k) => `${bucket}.${k}`).join(',');
  const terms = { terms: { field: g.field, size: a.topN, order: { [metricName]: 'desc' }, ...(a.minCount > 1 ? { min_doc_count: a.minCount } : {}) }, aggs: { [metricName]: metric } };
  const aggs = { filtered_data: { filter: withoutBlank(filterFor(c.srcId, a), g), aggs: { [gname]: terms } } };
  const cfg: Record<string, unknown> = { aggregationPaths: [gname], categoryLabel: g.cat };
  if (isMap) cfg.stateName = 'Bihar';
  return { queries: [{ ...envelope(c.srcId, a), filterPath, aggrQuery: { size: 0, aggs } }], cfg };
}

const PER = { day: 'Daily', week: 'Weekly', month: 'Monthly' };
const WHAT: Record<SourceId, string> = { reg: 'Registrations', enr: 'Enrollments', pass: 'Completions', rating: 'Ratings', assess: 'Assessments', question: 'Answers', activity: 'Sessions', custom: 'Records' };

// shape of professionalsRegistrationTrends / preServiceCumulativeRegistrations
export function buildTrend(c: RecipeContext): RecipeResult {
  const { a, metric, metricName, cumulative } = c;
  const per = PER[a.interval];
  const hname = cumulative ? `${per.toLowerCase()}_${WHAT[c.srcId].toLowerCase()}` : `${per} ${WHAT[c.srcId]}`;
  const inner: Record<string, EsNode> = { [metricName]: metric };
  if (cumulative) inner.cumulative_users = { cumulative_sum: { buckets_path: metricName } };
  const histogram = {
    date_histogram: { field: c.src.date, calendar_interval: a.interval, format: a.interval === 'month' ? 'yyyy-MM' : 'yyyy-MM-dd', min_doc_count: 0 },
    aggs: inner,
  };
  const aggs = { filtered_data: { filter: filterFor(c.srcId, a), aggs: { [hname]: histogram } } };
  const query: QueryObject = { ...envelope(c.srcId, a), aggrQuery: { size: 0, aggs } };
  if (cumulative) query.filterPath = `aggregations.filtered_data.${hname}.buckets.key,aggregations.filtered_data.${hname}.buckets.cumulative_users`;
  return { queries: [query], // The handler is picked by config.chartType, and 'bar' would send a date trend to the pie handler (epoch-ms labels); the chart shown is the visualization's own type.
    cfg: { chartType: 'line', aggregationPaths: [hname], isCumulative: cumulative, interval: a.interval, categoryLabel: 'Date' } };
}

/** More groups than any field has (facilities are the most, a few thousand), so the rate ranking sees all of them. */
const RATE_GROUP_LIMIT = 10000;

const DROPOUT_SCRIPT = 'params.total > 0 ? Math.round(params.dropped * 100.0 / params.total) : 0';

// "Not completed" means no completion at all (GXACA-1028), so a person with one finished and one open enrolment is not counted.
// The learners are counted once, then the ones with any completed enrolment are taken away.
const NO_COMPLETION_SCRIPT = 'params.total > 0 ? Math.round((params.total - params.done) * 100.0 / params.total) : 0';

interface RatePart {
  agg: Record<string, EsNode>;
  paths: Record<string, string>;
  script: string;
}

// What is counted per group besides everyone: the matching learners, or the learners with a completed enrolment to take away.
function ratePart(c: RecipeContext): RatePart {
  const unique = { aggs: { unique_users: { cardinality: { field: 'userId' } } } };
  if (c.a.rateSeg === NOT_COMPLETED_ID) {
    return { agg: { any_completed: { filter: { terms: { enrollStatus: ['2'] } }, ...unique } }, paths: { done: 'any_completed>unique_users' }, script: NO_COMPLETION_SCRIPT };
  }
  return { agg: { not_completed: { filter: traitClause(c.a, c.src), ...unique } }, paths: { dropped: 'not_completed>unique_users' }, script: DROPOUT_SCRIPT };
}

// shape of instituteDropoutRateByCompetency / dropoutRateByDistrict
export function buildRate(c: RecipeContext): RecipeResult {
  const { a, group: g } = c;
  const gname = snake(g.id);
  const part = ratePart(c);
  const sub = {
    total_enrolled: { cardinality: { field: 'userId' } },
    ...part.agg,
    dropout_rate: { bucket_script: { buckets_path: { total: 'total_enrolled', ...part.paths }, script: part.script } },
    sort_by_rate: { bucket_sort: { sort: [{ dropout_rate: { order: 'desc' } }], size: a.topN } },
  };
  // Every group is taken, then the top N by rate are kept: cutting to the biggest groups first would drop a small group with a high rate.
  const terms = { terms: { field: g.field, size: Math.max(RATE_GROUP_LIMIT, a.topN), order: { _count: 'desc' } }, aggs: sub };
  const aggs = { filtered_data: { filter: withoutBlank(filterFor(c.srcId, a), g), aggs: { [gname]: terms } } };
  const filterPath = `aggregations.filtered_data.${gname}.buckets.key,aggregations.filtered_data.${gname}.buckets.dropout_rate`;
  return { queries: [{ ...envelope(c.srcId, a), filterPath, aggrQuery: { size: 0, aggs } }], cfg: { aggregationPaths: [gname] } };
}

// shape of notEnrolledStudents / notCompletedProfessionals
export function buildGap(c: RecipeContext): RecipeResult {
  const { a } = c;
  const bName = a.srcB === 'enr' ? 'enrolledUsersAgg' : 'completedUsersAgg';
  const rows = (size: number) => ({ rows: { terms: { field: 'userId', size } } });
  // The starting list is everyone of the chosen cadre; channel, level and similar groups define who has reached the second step.
  const first = { registeredUsersAgg: { filter: filterFor(c.srcId, a, true), aggs: rows(10000) } };
  const second = { [bName]: { filter: filterFor(c.srcBId, a), aggs: rows(20000) } };
  return {
    queries: [
      { ...envelope(c.srcId, a), filterPath: 'aggregations.registeredUsersAgg.rows.buckets', aggrQuery: { size: 0, aggs: first } },
      { ...envelope(c.srcBId, a), filterPath: `aggregations.${bName}.rows.buckets`, aggrQuery: { size: 0, aggs: second } },
    ],
    cfg: { aggregationPaths: ['registeredUsersAgg', bName], drillChart: c.code + 'Detail' },
  };
}

const sortConfig = (cols: Column[]): Record<string, string> => {
  const latest = sortColumn(cols);
  return latest ? { sortColumn: latest.label, sortOrder: 'desc' } : {};
};

// shape of registeredStudentsDetail / enrolledProfessionalsDetail
export function buildList(c: RecipeContext): RecipeResult {
  const { a, code } = c;
  const cols = listColumns(a);
  const { sub, types, order } = columnAggs(cols);
  const rows = { terms: { field: 'userId', size: a.rows, ...(order ? { order } : {}) }, aggs: sub };
  const aggs = { [code]: { filter: filterFor(c.srcId, a), aggs: { rows } } };
  const query: QueryObject = { ...envelope(c.srcId, a), filterPath: `aggregations.${code}.rows.buckets`, aggrQuery: { size: 0, aggs } };
  return {
    queries: [query],
    cfg: { aggregationPaths: cols.map((col) => col.label), pathDataTypeMapping: [types], excludeKeyColumn: true, titleCaseKey: true, plotLabel: 'Name', ...sortConfig(cols) },
  };
}
